/*
  Toasty Golf - Site Events Tracker
  File: assets/tg-site-events.js
  Version: 0.4.4
  Purpose:
    - Create anonymous visitor/session/event identifiers only when analytics processing is allowed.
    - Build normalized analytics payloads.
    - Track add-to-cart success, cart quantity changes, and product removals.
    - Normalize Shopify Ajax cent amounts to decimal currency values before posting.
    - Log diagnostics only when debug is enabled.
    - Optionally POST anonymous events to TG Event Ops Web App when runtime config enables it.
    - No PII.
    - Attach Shopify Customer Privacy getRegion country/region fields when available.
    - No dependency on Shopify Custom Pixel.

  Privacy / consent:
    - Uses Shopify Customer Privacy API when available.
    - Strict mode: tracking is blocked unless analyticsProcessingAllowed() === true.
    - Does not create persistent localStorage IDs before analytics permission is allowed.
    - If analytics permission becomes blocked, Toasty Golf tracker IDs are cleared.

  Safety:
    - Does not block add-to-cart, cart drawer, or checkout.
    - Does not throw errors outward.
    - Does not hardcode endpoint URLs, tokens, or externally managed configuration in this asset.
    - Reads runtime config from window.TG_SITE_EVENTS_CONFIG / window.TGSiteEventsConfig injected by Liquid/theme settings.
    - If localStorage is unavailable after consent is allowed, falls back to in-memory IDs for the current page only.
*/

(function () {
  'use strict';

  if (window.TGSiteEvents && window.TGSiteEvents.__loaded) return;

  var CONFIG = {
    schemaVersion: '1.0',
    trackerVersion: '0.4.4',
    sourceSystem: 'shopify_theme',
    sessionTimeoutMs: 30 * 60 * 1000,
    debug: false,
    postEnabled: false,
    endpointUrl: '',
    requireAnalyticsConsent: true,
    postMaxBodyLength: 60000,
    privacyLoadMaxAttempts: 20,
    privacyLoadRetryMs: 250,
  };

  var KEYS = {
    visitorId: 'tg_site_events_visitor_id',
    sessionId: 'tg_site_events_session_id',
    visitorSessionSeq: 'tg_site_events_visitor_session_seq',
    sessionEventIndex: 'tg_site_events_session_event_index',
    lastActivityTs: 'tg_site_events_last_activity_ts',
  };

  var memoryStore = {};
  var cachedState = null;

  var privacyState = {
    ready: false,
    allowed: false,
    blockedReason: 'privacy_api_not_ready',
    loadAttempts: 0,
    loadError: '',
  };

  function debugLog(message, data) {
    if (!CONFIG.debug || !window.console || typeof window.console.log !== 'function') return;

    try {
      if (typeof data === 'undefined') {
        console.log('[TGSiteEvents] ' + message);
      } else {
        console.log('[TGSiteEvents] ' + message, data);
      }
    } catch (error) {
      // ignore
    }
  }

  function debugWarn(message, data) {
    if (!CONFIG.debug || !window.console || typeof window.console.warn !== 'function') return;

    try {
      if (typeof data === 'undefined') {
        console.warn('[TGSiteEvents] ' + message);
      } else {
        console.warn('[TGSiteEvents] ' + message, data);
      }
    } catch (error) {
      // ignore
    }
  }

  function getRuntimeConfigObject() {
    try {
      return window.TG_SITE_EVENTS_CONFIG || window.TGSiteEventsConfig || {};
    } catch (error) {
      return {};
    }
  }

  function toConfigBoolean(value, fallback) {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
      var clean = value.trim().toLowerCase();
      if (clean === 'true' || clean === '1' || clean === 'yes') return true;
      if (clean === 'false' || clean === '0' || clean === 'no') return false;
    }
    return fallback;
  }

  function applyRuntimeConfig() {
    var runtimeConfig = getRuntimeConfigObject();

    try {
      if (typeof runtimeConfig.endpointUrl === 'string') {
        CONFIG.endpointUrl = runtimeConfig.endpointUrl.trim();
      }

      if (typeof runtimeConfig.postEnabled !== 'undefined') {
        CONFIG.postEnabled = toConfigBoolean(runtimeConfig.postEnabled, CONFIG.postEnabled);
      }

      if (typeof runtimeConfig.debug !== 'undefined') {
        CONFIG.debug = toConfigBoolean(runtimeConfig.debug, CONFIG.debug);
      }

      if (typeof runtimeConfig.requireAnalyticsConsent !== 'undefined') {
        CONFIG.requireAnalyticsConsent = toConfigBoolean(
          runtimeConfig.requireAnalyticsConsent,
          CONFIG.requireAnalyticsConsent
        );
      }
    } catch (error) {
      // Runtime config must never break storefront behavior.
    }
  }

  applyRuntimeConfig();

  function safeGetStorageItem(key) {
    try {
      if (!window.localStorage) return memoryStore[key] || null;
      return window.localStorage.getItem(key);
    } catch (error) {
      return memoryStore[key] || null;
    }
  }

  function safeSetStorageItem(key, value) {
    try {
      if (!window.localStorage) {
        memoryStore[key] = String(value);
        return;
      }
      window.localStorage.setItem(key, String(value));
    } catch (error) {
      memoryStore[key] = String(value);
    }
  }

  function safeRemoveStorageItem(key) {
    try {
      if (window.localStorage) window.localStorage.removeItem(key);
    } catch (error) {
      // ignore
    }
    try {
      delete memoryStore[key];
    } catch (error) {
      // ignore
    }
  }

  function clearStoredStateForPrivacy() {
    safeRemoveStorageItem(KEYS.visitorId);
    safeRemoveStorageItem(KEYS.sessionId);
    safeRemoveStorageItem(KEYS.visitorSessionSeq);
    safeRemoveStorageItem(KEYS.sessionEventIndex);
    safeRemoveStorageItem(KEYS.lastActivityTs);
    cachedState = null;
  }

  function hasCustomerPrivacyApi() {
    return !!(
      window.Shopify &&
      window.Shopify.customerPrivacy &&
      typeof window.Shopify.customerPrivacy.analyticsProcessingAllowed === 'function'
    );
  }

  function refreshPrivacyState() {
    try {
      if (!CONFIG.requireAnalyticsConsent) {
        privacyState.ready = true;
        privacyState.allowed = true;
        privacyState.blockedReason = '';
        privacyState.loadError = '';
        return privacyState;
      }

      if (!hasCustomerPrivacyApi()) {
        privacyState.ready = false;
        privacyState.allowed = false;
        privacyState.blockedReason = 'privacy_api_not_available';
        clearStoredStateForPrivacy();
        return privacyState;
      }

      privacyState.ready = true;
      privacyState.allowed = window.Shopify.customerPrivacy.analyticsProcessingAllowed() === true;
      privacyState.blockedReason = privacyState.allowed ? '' : 'analytics_processing_not_allowed';
      privacyState.loadError = '';

      if (!privacyState.allowed) {
        clearStoredStateForPrivacy();
      }

      return privacyState;
    } catch (error) {
      privacyState.ready = false;
      privacyState.allowed = false;
      privacyState.blockedReason = 'privacy_api_error';
      privacyState.loadError = error && error.message ? error.message : String(error);
      clearStoredStateForPrivacy();
      return privacyState;
    }
  }

  function canTrackAnalytics() {
    var state = refreshPrivacyState();
    return state.ready === true && state.allowed === true;
  }

  function loadCustomerPrivacyApi() {
    try {
      if (hasCustomerPrivacyApi()) {
        refreshPrivacyState();
        debugLog('privacy ready', {
          analytics_allowed: privacyState.allowed,
          blocked_reason: privacyState.blockedReason,
        });
        return;
      }

      if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') {
        privacyState.loadAttempts += 1;

        if (privacyState.loadAttempts <= CONFIG.privacyLoadMaxAttempts) {
          window.setTimeout(loadCustomerPrivacyApi, CONFIG.privacyLoadRetryMs);
        } else {
          refreshPrivacyState();
          debugWarn('privacy API unavailable; analytics tracking blocked', {
            blocked_reason: privacyState.blockedReason,
          });
        }

        return;
      }

      window.Shopify.loadFeatures(
        [
          {
            name: 'consent-tracking-api',
            version: '0.1',
          },
        ],
        function (error) {
          if (error) {
            privacyState.ready = false;
            privacyState.allowed = false;
            privacyState.blockedReason = 'privacy_api_load_error';
            privacyState.loadError = error && error.message ? error.message : String(error);
            clearStoredStateForPrivacy();

            debugWarn('privacy API load error; analytics tracking blocked', {
              error: privacyState.loadError,
            });
            return;
          }

          refreshPrivacyState();
          debugLog('privacy ready', {
            analytics_allowed: privacyState.allowed,
            blocked_reason: privacyState.blockedReason,
          });
        }
      );
    } catch (error) {
      privacyState.ready = false;
      privacyState.allowed = false;
      privacyState.blockedReason = 'privacy_api_load_exception';
      privacyState.loadError = error && error.message ? error.message : String(error);
      clearStoredStateForPrivacy();

      debugWarn('privacy API exception; analytics tracking blocked', {
        error: privacyState.loadError,
      });
    }
  }

  function bindConsentChangeListener() {
    try {
      document.addEventListener('visitorConsentCollected', function (event) {
        refreshPrivacyState();

        debugLog('visitorConsentCollected', {
          analytics_allowed: privacyState.allowed,
          blocked_reason: privacyState.blockedReason,
          detail: event && event.detail ? event.detail : null,
        });
      });
    } catch (error) {
      // ignore
    }
  }

  function createUuid() {
    try {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
      }
    } catch (error) {
      // fallback below
    }

    // Fallback UUID-like random string. Used only if crypto.randomUUID is unavailable.
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (char) {
      var random = Math.random() * 16 | 0;
      var value = char === 'x' ? random : (random & 0x3) | 0x8;
      return value.toString(16);
    });
  }

  function createId(prefix) {
    return prefix + '_' + createUuid();
  }

  function nowMs() {
    return Date.now ? Date.now() : new Date().getTime();
  }

  function nowIso() {
    return new Date().toISOString();
  }

  function utcDateFromIso(isoString) {
    return String(isoString || '').slice(0, 10);
  }

  function toInt(value, fallback) {
    var number = parseInt(value, 10);
    return Number.isFinite(number) ? number : fallback;
  }

  function toFloat(value, fallback) {
    var number = parseFloat(value);
    return Number.isFinite(number) ? number : fallback;
  }

  function cleanString(value, maxLength) {
    if (value === null || typeof value === 'undefined') return '';
    var text = String(value).trim();
    if (!maxLength) return text;
    return text.slice(0, maxLength);
  }

  function getSafeUrlObject(url) {
    try {
      return new URL(url, window.location.origin);
    } catch (error) {
      return null;
    }
  }

  function getCleanPageUrl() {
    var url = getSafeUrlObject(window.location.href);
    if (!url) return '';
    return url.origin + url.pathname;
  }

  function getPagePath() {
    try {
      return window.location.pathname || '';
    } catch (error) {
      return '';
    }
  }

  function getReferrerDomain() {
    var referrer = document.referrer;
    if (!referrer) return '';
    var url = getSafeUrlObject(referrer);
    return url ? url.hostname : '';
  }

  function getUtmValue(name) {
    try {
      var params = new URLSearchParams(window.location.search || '');
      return cleanString(params.get(name), 200);
    } catch (error) {
      return '';
    }
  }

  function getDeviceType() {
    var width = getViewportWidth();

    if (width <= 749) return 'mobile';
    if (width <= 989) return 'tablet';
    return 'desktop';
  }

  function getViewportWidth() {
    try {
      return window.innerWidth || document.documentElement.clientWidth || 0;
    } catch (error) {
      return 0;
    }
  }

  function getViewportHeight() {
    try {
      return window.innerHeight || document.documentElement.clientHeight || 0;
    } catch (error) {
      return 0;
    }
  }

  function getScreenWidth() {
    try {
      return window.screen && window.screen.width ? window.screen.width : 0;
    } catch (error) {
      return 0;
    }
  }

  function getScreenHeight() {
    try {
      return window.screen && window.screen.height ? window.screen.height : 0;
    } catch (error) {
      return 0;
    }
  }

  function getBrowserLanguage() {
    try {
      return cleanString(navigator.language || navigator.userLanguage || '', 50);
    } catch (error) {
      return '';
    }
  }

  function getShopifyPrivacyRegionCode() {
    try {
      if (
        window.Shopify &&
        window.Shopify.customerPrivacy &&
        typeof window.Shopify.customerPrivacy.getRegion === 'function'
      ) {
        return cleanString(window.Shopify.customerPrivacy.getRegion(), 10).toUpperCase();
      }
    } catch (error) {
      // ignore
    }

    return '';
  }

  function getCountryCodeFromRegionCode(regionCode) {
    var text = cleanString(regionCode, 10).toUpperCase();

    if (/^[A-Z]{2}/.test(text)) {
      return text.slice(0, 2);
    }

    return '';
  }

  function getCountryNameFromCode(countryCode) {
    var code = cleanString(countryCode, 2).toUpperCase();
    var names = {
      US: 'United States',
      CA: 'Canada',
    };

    return names[code] || '';
  }

  function getCountryContext() {
    var regionCode = getShopifyPrivacyRegionCode();
    var countryCode = getCountryCodeFromRegionCode(regionCode);

    return {
      country_code: countryCode,
      country_region_code: regionCode,
      country_name: getCountryNameFromCode(countryCode),
      country_source: regionCode ? 'shopify_customer_privacy_get_region' : '',
    };
  }

  function getValidEndpointUrl() {
    var endpointUrl = cleanString(CONFIG.endpointUrl, 2000);
    var parsedUrl;

    if (!endpointUrl) return '';

    parsedUrl = getSafeUrlObject(endpointUrl);
    if (!parsedUrl || parsedUrl.protocol !== 'https:') {
      debugWarn('POST disabled; endpoint URL is invalid or not HTTPS');
      return '';
    }

    if (parsedUrl.href.indexOf('/exec') === -1) {
      debugWarn('POST disabled; endpoint URL must be the published /exec Web App URL, not /dev');
      return '';
    }

    return parsedUrl.href;
  }

  function extractProductHandleFromUrl(urlValue) {
    var url = getSafeUrlObject(urlValue);
    var path;
    var match;

    if (!url) return '';

    path = url.pathname || '';
    match = path.match(/\/products\/([^/?#]+)/);
    return match && match[1] ? cleanString(match[1], 250) : '';
  }

  function ensureState() {
    if (!canTrackAnalytics()) {
      return null;
    }

    var currentTs = nowMs();

    var visitorId = safeGetStorageItem(KEYS.visitorId);
    if (!visitorId) {
      visitorId = createId('v');
      safeSetStorageItem(KEYS.visitorId, visitorId);
    }

    var sessionId = safeGetStorageItem(KEYS.sessionId);
    var visitorSessionSeq = toInt(safeGetStorageItem(KEYS.visitorSessionSeq), 0);
    var sessionEventIndex = toInt(safeGetStorageItem(KEYS.sessionEventIndex), 0);
    var lastActivityTs = toInt(safeGetStorageItem(KEYS.lastActivityTs), 0);

    var shouldStartNewSession = !sessionId || !lastActivityTs || (currentTs - lastActivityTs > CONFIG.sessionTimeoutMs);

    if (shouldStartNewSession) {
      sessionId = createId('s');
      visitorSessionSeq += 1;
      sessionEventIndex = 0;

      safeSetStorageItem(KEYS.sessionId, sessionId);
      safeSetStorageItem(KEYS.visitorSessionSeq, visitorSessionSeq);
      safeSetStorageItem(KEYS.sessionEventIndex, sessionEventIndex);
    }

    safeSetStorageItem(KEYS.lastActivityTs, currentTs);

    cachedState = {
      tg_session_id: sessionId,
      tg_visitor_id: visitorId,
      visitor_session_seq: visitorSessionSeq,
      session_event_index: sessionEventIndex,
      last_activity_ts: currentTs,
    };

    return cachedState;
  }

  function nextEventState() {
    var state = ensureState();
    if (!state) return null;

    var nextIndex = toInt(state.session_event_index, 0) + 1;

    safeSetStorageItem(KEYS.sessionEventIndex, nextIndex);
    safeSetStorageItem(KEYS.lastActivityTs, nowMs());

    cachedState = {
      tg_session_id: state.tg_session_id,
      tg_visitor_id: state.tg_visitor_id,
      visitor_session_seq: state.visitor_session_seq,
      session_event_index: nextIndex,
      last_activity_ts: nowMs(),
    };

    return cachedState;
  }

  function normalizeMoneyCents(value) {
    if (value === null || typeof value === 'undefined' || value === '') return null;

    var number = toFloat(value, null);
    if (number === null) return null;

    // Shopify Ajax money amounts are integer minor units for USD storefronts.
    // Store TG Event Ops operational money fields as decimal currency values.
    return Math.round((number / 100) * 100) / 100;
  }

  function normalizeCartItem(item) {
    item = item || {};

    var quantity = toInt(item.quantity, null);
    var price = normalizeMoneyCents(
      typeof item.final_price !== 'undefined' ? item.final_price : item.price
    );

    var lineValue = normalizeMoneyCents(
      typeof item.final_line_price !== 'undefined'
        ? item.final_line_price
        : (typeof item.line_price !== 'undefined' ? item.line_price : null)
    );

    if (lineValue === null && price !== null && quantity !== null) {
      lineValue = price * quantity;
    }

    return {
      product_id: cleanString(item.product_id, 80),
      variant_id: cleanString(
        typeof item.variant_id !== 'undefined' ? item.variant_id : item.id,
        80
      ),
      sku: cleanString(item.sku, 120),
      product_title: cleanString(item.product_title || item.title, 250),
      variant_title: cleanString(item.variant_title, 250),
      quantity: quantity,
      price: price,
      line_value: lineValue,
      currency: cleanString(window.Shopify && window.Shopify.currency && window.Shopify.currency.active, 10),
      product_handle: extractProductHandleFromUrl(item.url),
    };
  }

  function getFirstCartItemFromResponse(cartData) {
    if (!cartData || typeof cartData !== 'object') return {};

    if (Array.isArray(cartData.items) && cartData.items.length > 0) {
      return cartData.items[0] || {};
    }

    return cartData;
  }

  function getCartJsonUrl() {
    try {
      if (window.routes && typeof window.routes.cart_url === 'string' && window.routes.cart_url) {
        return window.routes.cart_url.replace(/\/$/, '') + '.js';
      }
    } catch (error) {
      // fallback below
    }

    return '/cart.js';
  }

  function fetchCartJsonSafely() {
    if (typeof window.fetch !== 'function') {
      return Promise.reject(new Error('fetch_unavailable'));
    }

    return window.fetch(getCartJsonUrl(), {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    }).then(function (response) {
      if (!response || !response.ok) {
        throw new Error('cart_json_fetch_failed');
      }
      return response.json();
    });
  }

  function findCartItemIndex(cartData, variantId) {
    if (!cartData || !Array.isArray(cartData.items) || !variantId) return null;

    for (var i = 0; i < cartData.items.length; i += 1) {
      var item = cartData.items[i] || {};
      if (String(item.variant_id || item.id || '') === String(variantId)) {
        return i + 1;
      }
    }

    return null;
  }

  function mergeAddToCartPayloadData(baseData, fullCartData) {
    var output = Object.assign({}, baseData || {});
    var variantId = output.variant_id || '';
    var matchingLineIndex = findCartItemIndex(fullCartData, variantId);

    if (matchingLineIndex !== null) {
      output.line_index = matchingLineIndex;
    }

    output.cart_item_count = getCartItemCountAfter(fullCartData);
    output.cart_total_price = getCartValueAfter(fullCartData);
    output.cart_total_value = getCartValueAfter(fullCartData);

    if (!output.cart_currency) {
      output.cart_currency =
        output.currency ||
        cleanString(window.Shopify && window.Shopify.currency && window.Shopify.currency.active, 10);
    }

    return output;
  }

  function buildBasePayload(eventName, extraData) {
    var eventTs = nowIso();
    var state = nextEventState();

    if (!state) {
      debugLog('tracking blocked', {
        event_name: cleanString(eventName, 100),
        blocked_reason: privacyState.blockedReason,
        analytics_allowed: privacyState.allowed,
      });
      return null;
    }

    var countryContext = getCountryContext();

    var payload = {
      event_id: createId('e'),
      tg_session_id: state.tg_session_id,
      tg_visitor_id: state.tg_visitor_id,
      visitor_session_seq: state.visitor_session_seq,
      session_event_index: state.session_event_index,
      event_name: cleanString(eventName, 100),

      event_ts_utc: eventTs,
      received_ts_utc: '',
      event_date_utc: utcDateFromIso(eventTs),

      tracker_version: CONFIG.trackerVersion,
      schema_version: CONFIG.schemaVersion,
      source_system: CONFIG.sourceSystem,

      page_path: getPagePath(),
      page_url_clean: getCleanPageUrl(),
      referrer_domain: getReferrerDomain(),

      utm_source: getUtmValue('utm_source'),
      utm_medium: getUtmValue('utm_medium'),
      utm_campaign: getUtmValue('utm_campaign'),
      utm_content: getUtmValue('utm_content'),
      utm_term: getUtmValue('utm_term'),

      device_type: getDeviceType(),
      viewport_width: getViewportWidth(),
      viewport_height: getViewportHeight(),
      screen_width: getScreenWidth(),
      screen_height: getScreenHeight(),
      browser_language: getBrowserLanguage(),
      country_code: countryContext.country_code,
      country_region_code: countryContext.country_region_code,
      country_name: countryContext.country_name,
      country_source: countryContext.country_source,
    };

    if (extraData && typeof extraData === 'object') {
      Object.keys(extraData).forEach(function (key) {
        payload[key] = extraData[key];
      });
    }

    return payload;
  }

  function logDebug(payload) {
    if (!CONFIG.debug || !window.console || !payload) return;

    try {
      if (typeof console.groupCollapsed === 'function') {
        console.groupCollapsed('[TGSiteEvents] ' + payload.event_name + ' #' + payload.session_event_index);
        console.log(payload);
        console.groupEnd();
      } else {
        console.log('[TGSiteEvents]', payload);
      }
    } catch (error) {
      // ignore
    }
  }

  function safeStringifyPayload(payload) {
    try {
      return JSON.stringify(payload || {});
    } catch (error) {
      return '';
    }
  }

  function shouldPostPayload() {
    if (!CONFIG.postEnabled) return false;
    return !!getValidEndpointUrl();
  }

  function postPayload(payload) {
    var endpointUrl;
    var body;
    var requestOptions;

    if (!payload || !shouldPostPayload()) return;

    try {
      if (typeof window.fetch !== 'function') {
        debugWarn('POST skipped; fetch unavailable');
        return;
      }

      endpointUrl = getValidEndpointUrl();
      body = safeStringifyPayload(payload);

      if (!endpointUrl || !body) return;

      if (body.length > CONFIG.postMaxBodyLength) {
        debugWarn('POST skipped; payload too large', {
          event_id: payload.event_id || '',
          event_name: payload.event_name || '',
          body_length: body.length,
        });
        return;
      }

      requestOptions = {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'text/plain;charset=utf-8',
        },
        body: body,
      };

      if (body.length < 60000) {
        requestOptions.keepalive = true;
      }

      window.fetch(endpointUrl, requestOptions).catch(function (error) {
        debugWarn('POST failed safely', {
          event_id: payload.event_id || '',
          event_name: payload.event_name || '',
          error: error && error.message ? error.message : String(error),
        });
      });
    } catch (error) {
      debugWarn('POST failed safely', {
        event_id: payload && payload.event_id ? payload.event_id : '',
        event_name: payload && payload.event_name ? payload.event_name : '',
        error: error && error.message ? error.message : String(error),
      });
    }
  }

  function sendPayload(payload) {
    if (!payload) return null;

    logDebug(payload);
    postPayload(payload);
    return payload;
  }

  function trackEvent(eventName, extraData) {
    try {
      if (!eventName) return null;

      var payload = buildBasePayload(eventName, extraData || {});
      return sendPayload(payload);
    } catch (error) {
      debugWarn('trackEvent failed safely', {
        error: error && error.message ? error.message : String(error),
      });
      return null;
    }
  }

  function trackAddToCartSuccess(input) {
    try {
      input = input || {};

      var cartData = input.cartData || {};
      var item = getFirstCartItemFromResponse(cartData);
      var normalizedItem = normalizeCartItem(item);

      if (!normalizedItem.variant_id && input.productVariantId) {
        normalizedItem.variant_id = cleanString(input.productVariantId, 80);
      }

      var baseData = {
        source: cleanString(input.source || 'product-form', 100),

        product_id: normalizedItem.product_id,
        variant_id: normalizedItem.variant_id,
        sku: normalizedItem.sku,
        product_title: normalizedItem.product_title,
        product_handle: normalizedItem.product_handle,
        variant_title: normalizedItem.variant_title,
        quantity: normalizedItem.quantity,
        currency: normalizedItem.currency,
        cart_currency: normalizedItem.currency,

        cart_item_count: getCartItemCountAfter(cartData),
        cart_total_price: getCartValueAfter(cartData),
        cart_total_value: getCartValueAfter(cartData),
      };

      if (typeof window.fetch !== 'function') {
        return trackEvent('add_to_cart_success', baseData);
      }

      fetchCartJsonSafely()
        .then(function (fullCartData) {
          trackEvent('add_to_cart_success', mergeAddToCartPayloadData(baseData, fullCartData));
        })
        .catch(function (error) {
          debugWarn('cart.js enrichment skipped for add_to_cart_success', {
            error: error && error.message ? error.message : String(error),
          });
          trackEvent('add_to_cart_success', baseData);
        });

      return null;
    } catch (error) {
      debugWarn('trackAddToCartSuccess failed safely', {
        error: error && error.message ? error.message : String(error),
      });
      return null;
    }
  }

  function normalizeLineData(data) {
    data = data || {};

    var item = normalizeCartItem(data);

    if (!item.product_id && data.productId) item.product_id = cleanString(data.productId, 80);
    if (!item.product_id && data.product_id) item.product_id = cleanString(data.product_id, 80);

    if (!item.variant_id && data.productVariantId) item.variant_id = cleanString(data.productVariantId, 80);
    if (!item.variant_id && data.variantId) item.variant_id = cleanString(data.variantId, 80);
    if (!item.variant_id && data.variant_id) item.variant_id = cleanString(data.variant_id, 80);

    if (!item.sku && data.sku) item.sku = cleanString(data.sku, 120);

    if (!item.product_title && data.productTitle) item.product_title = cleanString(data.productTitle, 250);
    if (!item.product_title && data.product_title) item.product_title = cleanString(data.product_title, 250);

    if (!item.variant_title && data.variantTitle) item.variant_title = cleanString(data.variantTitle, 250);
    if (!item.variant_title && data.variant_title) item.variant_title = cleanString(data.variant_title, 250);

    if (!item.product_handle && data.productHandle) item.product_handle = cleanString(data.productHandle, 250);
    if (!item.product_handle && data.product_handle) item.product_handle = cleanString(data.product_handle, 250);
    if (!item.product_handle && data.productUrl) item.product_handle = extractProductHandleFromUrl(data.productUrl);
    if (!item.product_handle && data.product_url) item.product_handle = extractProductHandleFromUrl(data.product_url);

    if (item.price === null && typeof data.price_cents !== 'undefined') {
      item.price = normalizeMoneyCents(data.price_cents);
    }
    if (item.line_value === null && typeof data.line_value_cents !== 'undefined') {
      item.line_value = normalizeMoneyCents(data.line_value_cents);
    }

    if (!item.currency && data.currency) item.currency = cleanString(data.currency, 10);

    if (item.quantity === null && typeof data.quantity_before !== 'undefined') {
      item.quantity = toInt(data.quantity_before, null);
    }
    if (item.quantity === null && typeof data.quantityBefore !== 'undefined') {
      item.quantity = toInt(data.quantityBefore, null);
    }

    return item;
  }

  function getCartItemCountAfter(cartData) {
    cartData = cartData || {};
    return toInt(cartData.item_count, null);
  }

  function getCartValueAfter(cartData) {
    cartData = cartData || {};
    return normalizeMoneyCents(cartData.total_price);
  }

  function getCartChangeBaseData(input) {
    input = input || {};

    var previousLine = normalizeLineData(input.previousLine || input.previous_line || {});
    var updatedLine = normalizeLineData(input.updatedLine || input.updated_line || {});
    var cartData = input.cartData || input.cart_data || {};

    function hasValue(value) {
      return value !== null && typeof value !== 'undefined' && value !== '';
    }

    function roundCurrency(value) {
      var number = toFloat(value, null);
      if (number === null) return null;
      return Math.round(number * 100) / 100;
    }

    function firstValue() {
      for (var i = 0; i < arguments.length; i += 1) {
        if (hasValue(arguments[i])) return arguments[i];
      }
      return null;
    }

    var quantityBefore = toInt(
      typeof input.quantityBefore !== 'undefined' ? input.quantityBefore : input.quantity_before,
      null
    );

    if (quantityBefore === null) {
      quantityBefore = toInt(previousLine.quantity, null);
    }

    var quantityAfter = toInt(
      typeof input.quantityAfter !== 'undefined' ? input.quantityAfter : input.quantity_after,
      null
    );

    if (quantityAfter === null) {
      quantityAfter = toInt(updatedLine.quantity, null);
    }

    var identityData = previousLine.variant_id ? previousLine : updatedLine;
    var isRemoved = quantityAfter === 0 || !updatedLine.variant_id;

    var previousLineValue = hasValue(previousLine.line_value) ? previousLine.line_value : null;
    var updatedLineValue = hasValue(updatedLine.line_value) ? updatedLine.line_value : null;
    var unitPrice = firstValue(updatedLine.price, previousLine.price);

    if (!isRemoved && !hasValue(updatedLineValue) && hasValue(unitPrice) && quantityAfter !== null) {
      updatedLineValue = roundCurrency(unitPrice * quantityAfter);
    }

    var currentLineValue = isRemoved ? 0 : firstValue(updatedLineValue, previousLineValue);
    var removedLineValue = isRemoved ? previousLineValue : null;

    return {
      source: cleanString(input.source || 'cart-items', 100),
      line_index: toInt(input.lineIndex || input.line_index, null),

      product_id: identityData.product_id || updatedLine.product_id || previousLine.product_id,
      variant_id:
        identityData.variant_id ||
        updatedLine.variant_id ||
        previousLine.variant_id ||
        cleanString(input.productVariantId || input.variantId, 80),
      sku: identityData.sku || updatedLine.sku || previousLine.sku,
      product_title: identityData.product_title || updatedLine.product_title || previousLine.product_title,
      variant_title: identityData.variant_title || updatedLine.variant_title || previousLine.variant_title,
      product_handle: identityData.product_handle || updatedLine.product_handle || previousLine.product_handle,

      previous_quantity: quantityBefore,
      new_quantity: quantityAfter,
      quantity_delta:
        quantityBefore !== null && quantityAfter !== null
          ? quantityAfter - quantityBefore
          : null,
      quantity: quantityAfter,

      price: unitPrice,
      line_value: currentLineValue,
      previous_line_value: previousLineValue,
      new_line_value: currentLineValue,
      removed_line_value: removedLineValue,
      currency:
        identityData.currency ||
        updatedLine.currency ||
        previousLine.currency ||
        cleanString(window.Shopify && window.Shopify.currency && window.Shopify.currency.active, 10),

      cart_item_count: getCartItemCountAfter(cartData),
      cart_total_price: getCartValueAfter(cartData),
      cart_total_value: getCartValueAfter(cartData),
      cart_currency:
        identityData.currency ||
        updatedLine.currency ||
        previousLine.currency ||
        cleanString(window.Shopify && window.Shopify.currency && window.Shopify.currency.active, 10),
    };
  }

  function trackCartQuantityChanged(input) {
    try {
      var data = getCartChangeBaseData(input);

      return trackEvent('cart_quantity_changed', data);
    } catch (error) {
      debugWarn('trackCartQuantityChanged failed safely', {
        error: error && error.message ? error.message : String(error),
      });
      return null;
    }
  }

  function trackProductRemovedFromCart(input) {
    try {
      var data = getCartChangeBaseData(input);
      data.new_quantity = 0;
      data.quantity = 0;

      if (data.previous_quantity !== null) {
        data.quantity_delta = 0 - data.previous_quantity;
        data.removed_quantity = data.previous_quantity;
      }

      return trackEvent('product_removed_from_cart', data);
    } catch (error) {
      debugWarn('trackProductRemovedFromCart failed safely', {
        error: error && error.message ? error.message : String(error),
      });
      return null;
    }
  }


  function getPublicConfig() {
    var countryContext = getCountryContext();

    return {
      schemaVersion: CONFIG.schemaVersion,
      trackerVersion: CONFIG.trackerVersion,
      sourceSystem: CONFIG.sourceSystem,
      debug: CONFIG.debug,
      postEnabled: CONFIG.postEnabled,
      endpointConfigured: !!CONFIG.endpointUrl,
      countryCode: countryContext.country_code,
      countryRegionCode: countryContext.country_region_code,
      countrySource: countryContext.country_source,
      requireAnalyticsConsent: CONFIG.requireAnalyticsConsent,
    };
  }

  window.TGSiteEvents = {
    __loaded: true,
    config: getPublicConfig(),
    trackEvent: trackEvent,
    trackAddToCartSuccess: trackAddToCartSuccess,
    trackCartQuantityChanged: trackCartQuantityChanged,
    trackProductRemovedFromCart: trackProductRemovedFromCart,
  };

  bindConsentChangeListener();
  loadCustomerPrivacyApi();

  if (CONFIG.debug) {
    try {
      console.log('[TGSiteEvents] loaded', {
        tracker_version: CONFIG.trackerVersion,
        schema_version: CONFIG.schemaVersion,
        post_enabled: CONFIG.postEnabled,
        endpoint_configured: !!CONFIG.endpointUrl,
        privacy: privacyState,
      });
    } catch (error) {
      // ignore
    }
  }
})();
